import Markdown from "markdown-to-jsx";

import { cn } from "@/common/utils/css.utils";
import {
  PLP_SEO_LEAF_HEADING_SIZE,
  PLP_SEO_SECTION_HEADING_SIZE,
} from "@/components/search/plp-seo/plp-seo.tokens";
import { Heading } from "@/ui/cva/Heading";

type ListingMarkdownProps = {
  markdown: string;
  className?: string;
};

/**
 * Strapi listing-page copy with the design-system tokens from WHO-5534: Noto Sans body
 * 16/24 (16/20 on mobile) in grey-800, brand-500 underlined links, Filson Pro headings.
 *
 * The copy's own `##`/`###` sit under the block's h2, so they render as h3/h4. Sizes follow
 * the quiet scale from review round 2: sub-headings at 18/28, deeper headings at 16px bold,
 * both in grey-800. The old PLP rendered both as h2, which flattened the outline on every
 * landing page.
 */
export const ListingMarkdown = ({
  markdown,
  className,
}: ListingMarkdownProps) => (
  <Markdown
    className={cn(
      "space-y-3 font-body text-base leading-5 text-grey-800 md:leading-6",
      className
    )}
    options={{
      forceBlock: true,
      overrides: {
        a: { props: { className: "text-brand-500 underline break-words" } },
        h2: {
          component: Heading,
          props: {
            as: "h3",
            size: PLP_SEO_SECTION_HEADING_SIZE,
            className: "pt-2 text-grey-800",
          },
        },
        h3: {
          component: Heading,
          props: {
            as: "h4",
            size: PLP_SEO_LEAF_HEADING_SIZE,
            className: "pt-1 text-grey-800",
          },
        },
        h4: {
          component: Heading,
          props: {
            as: "h4",
            size: PLP_SEO_LEAF_HEADING_SIZE,
            className: "text-grey-800",
          },
        },
        ul: { props: { className: "ml-5 list-outside list-disc space-y-1" } },
        ol: {
          props: { className: "ml-5 list-outside list-decimal space-y-1" },
        },
      },
    }}
  >
    {markdown}
  </Markdown>
);
